import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useLocation as useLocationContext } from '@/contexts/LocationContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useRole } from '@/hooks/use-role';
import { isInSetupMode, isSetupModeAllowedPath } from '@/features/onboarding/lib/setup-mode';
import { useOrgRoleSurfaces } from '@/hooks/useOrgRoleSurfaces';
import {
  NO_FLOOR_ROUTE,
  STATION_HOME_ROUTES,
  isDtfPrinterRole,
  isRouteAllowedForRole,
} from '@/lib/roles';
import AppLoadingScreen from './AppLoadingScreen';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const isNoFloorPath = (pathname: string): boolean =>
  (pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname) === NO_FLOOR_ROUTE;

/**
 * The extra rules for a DTF Printer: a route allowlist, and a floor of its own
 * (BLU-1335). With a floor it goes to its station home from anywhere off the
 * allowlist and from the no-floor screen; without one it goes to the no-floor
 * screen from everywhere else.
 *
 * Its own component so the surfaces hook runs only for this role, after
 * ProtectedRoute's early returns: every other role, and a signed-out visitor,
 * makes no extra request. The observer shares AppSidebar's query key and
 * staleTime, so a DTF Printer makes none either.
 *
 * No loop is possible. Every other redirect that can reach this role keys off
 * `hidesProduction`, which is part of the rule below: ProductionAccessGuard and
 * AppSidebar. HomeRouteSwitch keys off `isPureClient` but cannot reach a DTF
 * Printer, because `/` is off its allowlist. Floor counts include inactive
 * floors (claim-flow.service.ts, locations.dao.ts), so `hidesProduction` true
 * implies the list is empty.
 */
function DtfFloorGate({ role, children }: { role: string; children: React.ReactNode }) {
  const { pathname } = useLocation();
  const { locations, locationsListed } = useLocationContext();
  const { hidesProduction } = useOrgRoleSurfaces({ refetchOnMount: false, retryOnMount: false });

  const atNoFloor = isNoFloorPath(pathname);
  const noFloor = hidesProduction || (locationsListed && locations.length === 0);

  if (noFloor) {
    return atNoFloor ? <>{children}</> : <Navigate to={NO_FLOOR_ROUTE} replace />;
  }

  const home = STATION_HOME_ROUTES[role];
  if (home && (atNoFloor || !isRouteAllowedForRole(role, pathname))) {
    return <Navigate to={home} replace />;
  }

  return <>{children}</>;
}

/**
 * The app's route guard. Mounted around the AppLayout routes AND, on their own,
 * the TV routes and `/no-floor`, so every rule below covers those too.
 *
 * Order after the auth, location and profile loading returns:
 * 1. First-load gate (BLU-1376): while the session's FIRST org load is still in
 *    flight, a user who is not a platform admin sees the loading screen, so no
 *    page mounts (and no sidebar paints) before the org's setup state is known.
 *    It reads the provider's `initialised` flag, which never goes back to
 *    false: an org switch never shows the screen and never unmounts the app.
 *    When the first load fails, the rules below fail open.
 * 2. Setup mode (BLU-1376, handover 4.5): a new org that has not finished
 *    setup reaches only the wizard and the pages it opens; anything else goes
 *    to `/onboarding`. It runs BEFORE the DTF Printer and station-home rules
 *    and skips them, because those would bounce to a station home that is off
 *    the setup allowlist (a redirect loop). UX only: data stays fenced by RLS
 *    and the procedures. Platform admins and 3PL providers are never in it.
 * 3. DTF Printer floor gate, the no-floor screen, station homes.
 */
export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, loading: authLoading, profile } = useAuth();
  const { isLoading: locationLoading } = useLocationContext();
  const { organization, initialised } = useOrganization();
  const { isPlatformAdmin } = useRole();
  const location = useLocation();
  const { t } = useTranslation('auth');

  // Show loading screen while auth is loading
  if (authLoading) {
    return <AppLoadingScreen message={t('authenticating')} />;
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Show loading screen while locations are loading (after auth is complete)
  if (locationLoading) {
    return <AppLoadingScreen message={t('loadingLocations')} />;
  }

  // Additional check for profile completeness
  if (!profile) {
    return <AppLoadingScreen message={t('loadingProfile')} />;
  }

  const platformAdmin = isPlatformAdmin();

  // 1. The session's first org load has not settled yet.
  if (!platformAdmin && organization === null && !initialised) {
    return <AppLoadingScreen message={t('loadingSession')} />;
  }

  // 2. Setup mode: the wizard and its pages only; the station rules never run.
  if (isInSetupMode(organization, platformAdmin)) {
    return isSetupModeAllowedPath(location.pathname) ? <>{children}</> : <Navigate to="/onboarding" replace />;
  }

  if (isDtfPrinterRole(profile.role)) {
    return <DtfFloorGate role={profile.role}>{children}</DtfFloorGate>;
  }

  // The no-floor screen is for a DTF Printer only; anyone else goes home.
  if (isNoFloorPath(location.pathname)) {
    return <Navigate to={STATION_HOME_ROUTES[profile.role] ?? '/'} replace />;
  }

  // Roles held to a route allowlist go back to their station home from anywhere
  // else. Only the DTF Printer has an entry today, and it is handled above.
  if (!isRouteAllowedForRole(profile.role, location.pathname)) {
    const home = STATION_HOME_ROUTES[profile.role];
    if (home) return <Navigate to={home} replace />;
  }

  return <>{children}</>;
}
